import { useTranslation } from '@/i18n';
import yup from '@/libs/validation';
import { useCallback, useMemo } from 'react';
import { ValidationError } from 'yup';

type ValidationResult = {
  min_chars: ValidationError | undefined;
  max_chars: ValidationError | undefined;
};

export const useMinAndMaxValidation = (maxChars: number) => {
  const { t } = useTranslation();
  const schema = useMemo(
    () =>
      yup.object().shape({
        min_chars: yup
          .number()
          .integer(t, '', t('transaction_items.validation_message.integer'))
          .required(t('transaction_items.validation_message.required'))
          .min(0)
          .max(
            maxChars,
            t('transaction_items.validation_message.maxChars', { maxChars })
          ),
        max_chars: yup
          .number()
          .integer(t, '', t('transaction_items.validation_message.integer'))
          .required(t('transaction_items.validation_message.required'))
          .min(0)
          .max(
            maxChars,
            t('transaction_items.validation_message.maxChars', { maxChars })
          )
          .test(
            'maxGreaterThanMin',
            t('transaction_items.validation_message.maxGreaterThanMin'),
            (value, context) => {
              if (
                context.parent.min_chars !== null &&
                value <= context.parent.min_chars
              ) {
                return false;
              }
              return true;
            }
          ),
      }),
    [maxChars, t]
  );

  const validate: (
    minChars: number | undefined,
    maxChars: number | undefined
  ) => Promise<ValidationResult> = useCallback(
    async (minChars: number | undefined, maxChars: number | undefined) => {
      try {
        await schema.validate(
          {
            min_chars: minChars,
            max_chars: maxChars,
          },
          {
            abortEarly: false,
          }
        );
      } catch (error) {
        if (error instanceof yup.ValidationError) {
          const errors: ValidationResult = {
            min_chars: undefined,
            max_chars: undefined,
          };

          error.inner.forEach((err) => {
            if (err.path === 'min_chars') {
              errors.min_chars = err;
            }
            if (err.path === 'max_chars') {
              errors.max_chars = err;
            }
          });
          return errors;
        }
      }
      return {
        min_chars: undefined,
        max_chars: undefined,
      };
    },
    [schema]
  );

  return { validate };
};
